import { useCallback, useMemo } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Typography } from '@vapor/material'
import { FormItem, HICLoading } from 'hic-components'
import { useForm, Controller } from 'react-hook-form'
import { useCreateChargeForReservationMutation } from 'src/api/api.generated'
import HicAmountField from 'src/app/components/hic-amount-field/hic-amount-field'
import { useTenant } from 'src/app/hooks/context/context.hook'
import { useCustomError } from 'src/utils/custom-errors.hook'
import * as z from 'zod'
import { useTranslation } from 'react-i18next'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'

type ChargeOnCreditCardProps = {
  propertyId: string
  reservationId: string
  description: string
  maxAmountAllowed?: number
  onCreationComplete: () => void
  refetchQueries?: string[]
}

export const ChargeOnCreditCard = (props: ChargeOnCreditCardProps) => {
  const { propertyId, reservationId, description, maxAmountAllowed, onCreationComplete, refetchQueries } = props
  const { id: tenantId, currency } = useTenant()
  const { t } = useTranslation()
  const { formatCurrency } = useFormatCurrency()
  const showError = useErrorSnackBar()

  const [createChargeForReservation, { loading }] = useCreateChargeForReservationMutation()

  const formattedMaxAmount = useMemo(() => (maxAmountAllowed ? formatCurrency(maxAmountAllowed, currency) : ''), [maxAmountAllowed, currency])

  const schema = z.object({
    amount: z.number().refine((data) => !maxAmountAllowed || data <= maxAmountAllowed, {
      message: t('pms.booking.reservation.chare_for_reservation.execute.max_amount_error', { maxAmount: formattedMaxAmount }),
    }),
  })

  type SchemaType = z.infer<typeof schema>
  const customErrorMap = useCustomError()
  z.setErrorMap(customErrorMap)
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<SchemaType>({ resolver: zodResolver(schema) })

  const submitFormHandler = useCallback(
    handleSubmit(({ amount }) => {
      const executeMutation = async (amount: number) => {
        await createChargeForReservation({
          variables: {
            input: {
              tenantId,
              propertyId,
              reservationId,
              payment: {
                amount,
                currency,
                description: description,
              },
            },
          },
          refetchQueries: refetchQueries ?? [],
          onCompleted: (data) => {
            if (data.createChargeForReservation.amount) onCreationComplete()
          },
          onError: (error) => {
            showError(error)
          },
        })
      }

      executeMutation(amount)
    }),
    [handleSubmit],
  )

  return (
    <>
      {loading && <HICLoading />}
      <Typography variant={'titleXSmall'}>{t('pms.booking.reservation.chare_for_reservation.execute.title')}</Typography>
      {maxAmountAllowed && <Typography variant={'caption'}>{t('pms.booking.reservation.chare_for_reservation.execute.max_amount', { maxAmount: formattedMaxAmount })}</Typography>}
      <Controller<SchemaType, 'amount'>
        name={'amount'}
        control={control}
        render={({ field }) => (
          <FormItem fullWidth errorMessage={errors.amount?.message}>
            <HicAmountField nonNegative currency={currency} value={field.value} onChange={field.onChange} error={errors.amount ? true : false} />
          </FormItem>
        )}
      />
      <Button
        variant="outlined"
        onClick={(_) => {
          submitFormHandler()
        }}
      >
        {t('pms.booking.reservation.chare_for_reservation.execute')}
      </Button>
    </>
  )
}
